import React, { useState, useMemo } from 'react';
import {
  InformationCircleIcon,
  ShieldCheckIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import TimelineView from './TimelineView';
import CopyButton from './CopyButton';

export default function SafeIPEventsTab({
  excludedSignIn = [],
  excludedExchange = [],
  excludedDirectory = [],
  excludedSharePoint = [],
  excludedMessageTrace = [],
  excludedUrlClicks = [],
  excludedIPs = [],
  totalExcluded = 0,
  totalEvents = 0,
  safeIPCount = 0,
  hideSafeIPs = true,
  onToggleHide,
  legAttribution = null,
}) {
  const [showTimeline, setShowTimeline] = useState(false);

  const ipSummary = useMemo(() => {
    const counts = {};
    const addCount = (evt, type) => {
      const ip = evt.IPAddress || 'Unknown';
      if (!counts[ip]) counts[ip] = { signIn: 0, exchange: 0, directory: 0, sharePoint: 0, msgTrace: 0, urlClick: 0 };
      counts[ip][type] += 1;
    };
    excludedSignIn.forEach((evt) => addCount(evt, 'signIn'));
    excludedExchange.forEach((evt) => addCount(evt, 'exchange'));
    excludedDirectory.forEach((evt) => addCount(evt, 'directory'));
    excludedSharePoint.forEach((evt) => addCount(evt, 'sharePoint'));
    excludedMessageTrace.forEach((evt) => addCount(evt, 'msgTrace'));
    excludedUrlClicks.forEach((evt) => addCount(evt, 'urlClick'));

    return Object.entries(counts)
      .map(([ip, c]) => ({
        ipAddress: ip,
        signInCount: c.signIn,
        exchangeCount: c.exchange,
        directoryCount: c.directory,
        sharePointCount: c.sharePoint,
        msgTraceCount: c.msgTrace,
        urlClickCount: c.urlClick,
        totalCount: c.signIn + c.exchange + c.directory + c.sharePoint + c.msgTrace + c.urlClick,
      }))
      .sort((a, b) => b.totalCount - a.totalCount);
  }, [excludedSignIn, excludedExchange, excludedDirectory, excludedSharePoint, excludedMessageTrace, excludedUrlClicks]);

  // Empty state: no safe IPs configured
  if (safeIPCount === 0) {
    return (
      <div className="card text-center py-16">
        <ShieldCheckIcon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-400 mb-2">No Safe IPs Flagged</h3>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          Mark IP addresses as "Safe" in the IP Addresses tab to filter out
          known-good traffic from your investigation results. Tenant-level safe IPs
          can be configured in the Admin tenant settings.
        </p>
      </div>
    );
  }

  // Empty state: safe IPs exist but no events match (or hiding is off)
  if (totalExcluded === 0) {
    return (
      <div className="card text-center py-16">
        <ShieldCheckIcon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-400 mb-2">
          {hideSafeIPs ? 'No Events Hidden' : 'Safe IP Hiding Disabled'}
        </h3>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          {hideSafeIPs
            ? `${safeIPCount} IP${safeIPCount !== 1 ? 's are' : ' is'} marked as safe, but none of the events in this investigation match those IPs.`
            : 'Safe IP hiding is currently off. Enable it to filter events from safe IPs.'}
        </p>
        {!hideSafeIPs && onToggleHide && (
          <button onClick={onToggleHide} className="btn-secondary mt-4 text-sm">
            Enable Safe IP Hiding
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="card bg-blue-900/20 border-blue-700/30">
        <div className="flex items-start gap-3">
          <InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-gray-300">
              <span className="font-semibold text-gray-100">{totalExcluded}</span> of{' '}
              <span className="font-semibold text-gray-100">{totalEvents}</span> events
              are from IP addresses marked as safe.
              {hideSafeIPs
                ? ' These events are hidden from the Timeline, Summary, and Raw Logs tabs.'
                : ' Safe IP hiding is currently off \u2014 all events are visible.'}
            </p>
          </div>
          {onToggleHide && (
            <button
              onClick={onToggleHide}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors flex-shrink-0 ${
                hideSafeIPs
                  ? 'bg-blue-900/40 border-blue-700/50 text-blue-300 hover:bg-blue-900/60'
                  : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-gray-300'
              }`}
            >
              {hideSafeIPs ? <EyeIcon className="h-3.5 w-3.5" /> : <EyeSlashIcon className="h-3.5 w-3.5" />}
              {hideSafeIPs ? 'Show events' : 'Hide events'}
            </button>
          )}
        </div>
      </div>

      {/* Summary Table */}
      <div className="card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-800">
          <h3 className="text-sm font-semibold text-gray-300">Safe IP Event Summary</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                  IP Address
                </th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                  Sign-In
                </th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                  Exchange
                </th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                  Directory
                </th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                  SharePoint
                </th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                  Msg Trace
                </th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                  URL Click
                </th>
                <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">
                  Total
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {ipSummary.map((row) => (
                <tr key={row.ipAddress} className="hover:bg-gray-800/50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-gray-200 group">{row.ipAddress}<CopyButton text={row.ipAddress} /></td>
                  <td className="px-4 py-3 text-center text-gray-400 font-mono">{row.signInCount}</td>
                  <td className="px-4 py-3 text-center text-gray-400 font-mono">{row.exchangeCount}</td>
                  <td className="px-4 py-3 text-center text-gray-400 font-mono">{row.directoryCount}</td>
                  <td className="px-4 py-3 text-center text-gray-400 font-mono">{row.sharePointCount}</td>
                  <td className="px-4 py-3 text-center text-gray-400 font-mono">{row.msgTraceCount}</td>
                  <td className="px-4 py-3 text-center text-gray-400 font-mono">{row.urlClickCount}</td>
                  <td className="px-4 py-3 text-center text-gray-300 font-mono font-semibold">{row.totalCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Expandable Sign-In Event Timeline */}
      {excludedSignIn.length > 0 && (
        <div>
          <button
            onClick={() => setShowTimeline(!showTimeline)}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200 transition-colors mb-3"
          >
            {showTimeline ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
            {showTimeline ? 'Hide' : 'Show'} safe IP sign-in events ({excludedSignIn.length})
          </button>
          {showTimeline && (
            <TimelineView signInEvents={excludedSignIn} legAttribution={legAttribution} />
          )}
        </div>
      )}
    </div>
  );
}
